import React, { useEffect, useState } from "react";
import { Trans } from "@lingui/macro";
import { Button } from "components/UI/Button/Button";
import { useDispatch, useSelector } from "react-redux";
import {
  NULL_VALUE,
  SelectBox,
  typeValuesListItem,
} from "components/UI/SelectBox/SelectBox";
import {
  IdUsersState,
  typeUsersFilters,
} from "model/dashboardUsers/dUsersReducer";
import { dUsersSetFiltersAC } from "model/dashboardUsers/dUsersActionCreators";
import { getStoreList } from "model/dashboardStores/dStoresAPI";
import { logoutSagaAC } from "model/system/systemActionCreators";
import { Loader } from "components/UI/Loader/Loader";

// Type
export type typeDUsersFilters = {
  setOpenFilterBar: React.Dispatch<React.SetStateAction<boolean>>;
  setIsActiveFilters: React.Dispatch<React.SetStateAction<boolean>>;
};

// Component
export const DUsersManagementFilters: React.FC<typeDUsersFilters> = ({
  setOpenFilterBar,
  setIsActiveFilters,
}) => {
  const dispatch = useDispatch();
  const filters = useSelector(
    (state: { dUsers: IdUsersState }) => state.dUsers.filters,
  );

  const [storeIdValue, setStoreIdValue] = useState<string>(
    () => filters.storeId || "",
  );
  const [selectStoreList, setSelectStoreList] = useState<typeValuesListItem[]>(
    [],
  );
  const [loaderStore, setLoaderStore] = useState(false);

  // Deleted SelectBox
  const DELETED = "deleted";
  const NOT_DELETED = "notDeleted";
  const deletedValueList: typeValuesListItem[] = [
    { id: NOT_DELETED, name: <Trans>Not deleted</Trans> },
    { id: DELETED, name: <Trans>Deleted</Trans> },
  ];
  const [deletedValue, setDeletedValue] = useState<string | null>(
    filters.deleted === null
      ? NULL_VALUE.id
      : filters.deleted
        ? DELETED
        : NOT_DELETED,
  );

  // Did mount
  useEffect(() => {
    setLoaderStore(true);
    const setData = async () => {
      let responseStores = await getStoreList({
        filters: { name: null },
        full: false,
      });

      if (responseStores.status === 200) {
        const newStoresList = [...selectStoreList, ...responseStores.data];
        setSelectStoreList(newStoresList);
      } else if (responseStores.status === 401) {
        dispatch(logoutSagaAC());
      }
      setLoaderStore(false);
    };

    setData().catch(console.error);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (filters.storeId) {
      setStoreIdValue(filters.storeId);
    }
  }, [filters]);

  const checkIsChangedFiltersHandler = (
    newFilters: typeUsersFilters,
  ): boolean => {
    return !(
      newFilters.deleted === filters.deleted &&
      newFilters.storeId === filters.storeId
    );
  };

  const onSubmitHandler = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const newFilters: typeUsersFilters = {
      storeId: storeIdValue === NULL_VALUE.id ? null : storeIdValue,
      deleted:
        deletedValue === DELETED
          ? true
          : deletedValue === NOT_DELETED
            ? false
            : null,
    };

    if (checkIsChangedFiltersHandler(newFilters)) {
      dispatch(dUsersSetFiltersAC(newFilters));
      setIsActiveFilters(true);
    }

    setOpenFilterBar(false);
  };

  const onReUsersHandler = () => {
    setIsActiveFilters(false);
    setStoreIdValue(NULL_VALUE.id);
    setDeletedValue(NOT_DELETED);

    dispatch(dUsersSetFiltersAC({ storeId: null, deleted: false }));
  };

  return (
    <>
      <form onSubmit={(e) => onSubmitHandler(e)}>
        <div className="flex flex-col gap-4">
          <div className={"relative"}>
            <SelectBox
              label={<Trans>Store</Trans>}
              valuesList={selectStoreList}
              valueId={storeIdValue}
              setIdValue={setStoreIdValue}
              labelClassname="text-left"
            />
            {loaderStore && <Loader />}
          </div>
          <div>
            <SelectBox
              label={<Trans>Employees</Trans>}
              valuesList={deletedValueList}
              valueId={deletedValue}
              setIdValue={setDeletedValue}
            />
          </div>

          <div className="flex gap-4 mt-4">
            <Button
              buttonClassName="w-full"
              secondary={true}
              type="button"
              label={<Trans>Clear</Trans>}
              onClick={() => onReUsersHandler()}
            />
            <Button
              buttonClassName="w-full"
              type="submit"
              label={<Trans>Apply</Trans>}
            />
          </div>
        </div>
      </form>
    </>
  );
};
